import { useState } from 'react'
import { AxiosError } from 'axios'
import { Link } from '@tanstack/react-router'
import { useDeleteChapter } from '@/services/chapterService'
import { ChapterItemType } from '@/types/module'
import { PagingInfo } from '@/types/user'
import { Trash } from 'lucide-react'
import { toast } from 'sonner'
import { useModulesStore } from '@/stores/useModulesStore'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import TablePaginate from '@/components/ui/table-paginate'
import { ConfirmDialog } from '@/components/confirm-dialog'

const QuestionListTable = ({
  meta,
  data,
  page,
  handlePageChange,
  refetchChapter,
}: {
  meta: PagingInfo
  data: ChapterItemType[]
  page: number
  handlePageChange: (page: number) => void
  refetchChapter: () => void
}) => {
  const { setSelectedChapter } = useModulesStore()
  const [deleteTarget, setDeleteTarget] = useState<ChapterItemType | null>(null)

  const { DeleteChapterAsync, isDeletingChapter } = useDeleteChapter()

  const collumns = [
    { title: 'Chapter', width: 100 },
    { title: 'Question', width: '20%' },
    { title: '', width: '10%' },
  ]

  const handleDelete = () => {
    DeleteChapterAsync(deleteTarget?.chapter_id ?? '')
      .then(() => {
        toast.success('Chapter deleted!')
        refetchChapter()
        handlePageChange(page)
        setDeleteTarget(null)
      })
      .catch((error: AxiosError) => {
        const errorMsg =
          (error.response?.data as { detail?: string })?.detail ||
          'Failed to delete chapter'
        toast.error(errorMsg, {
          duration: 3000,
          position: 'top-right',
        })
      })
  }

  return (
    <div>
      <Table>
        <TableHeader className='bg-[#F6F7F9]'>
          <TableRow>
            {collumns.map((item, index: number) => (
              <TableHead
                key={item.title}
                style={{ width: item.width }}
                className={cn(
                  'font-bold text-[#002652]',
                  index === 1 && 'text-center'
                )}
              >
                {item.title}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody className='text-[#002652]'>
          {data?.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} className='h-24 text-center'>
                No data found.
              </TableCell>
            </TableRow>
          )}
          {data.map((item: ChapterItemType) => (
            <TableRow key={item.chapter_id}>
              <TableCell>{item.chapter_name}</TableCell>
              <TableCell className='text-center'>
                {item.total_questions}
              </TableCell>
              <TableCell className='flex items-center justify-center gap-4'>
                <Link
                  to='/question-lib/$quesiontionLibId'
                  params={{ quesiontionLibId: item.chapter_id.toString() }}
                  search={{ source: 'questions' }}
                  className='rounded px-6 py-2 text-[#0057B8] hover:bg-[#0057B8]/10'
                  onClick={() => setSelectedChapter(item)}
                >
                  View detail
                </Link>
                <Button
                  disabled={isDeletingChapter}
                  variant='ghost'
                  className='px-3.5 py-2.5 hover:text-red-500'
                  onClick={() => setDeleteTarget(item)}
                >
                  <Trash />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {meta?.total_pages > 1 && (
        <div className='flex justify-end p-4'>
          <TablePaginate
            itemPerPage={meta.items_per_page}
            totalItems={meta.total_items}
            page={page}
            totalPages={meta?.total_pages}
            onPageChange={handlePageChange}
            readOnlyPageDisplay={false}
            hideOnSinglePage={false}
          />
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        isLoading={isDeletingChapter}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
        title='Delete Chapter'
        desc={`Please confirm that you want to delete chapter "${deleteTarget?.chapter_name}". This action cannot be reversed.`}
        confirmText='Delete'
        destructive
        handleConfirm={() => handleDelete()}
        handleClose={() => setDeleteTarget(null)}
      />
    </div>
  )
}

export default QuestionListTable
